import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Edit, Trash2, X, Check, Download, Loader2, Filter, Search, ChevronRight, ChevronDown, Trophy } from 'lucide-react';
import toast from 'react-hot-toast';
import { teamsService, competitionsService, Team, Competition, WizardTeam, getErrorMessage } from '@/services';

const teamSchema = z.object({
  competition_id: z.coerce.number().optional(),
  name: z.string().min(1, 'validation.required'),
  short_name: z.string().optional(),
  logo_url: z.string().optional(),
});

type TeamForm = z.infer<typeof teamSchema>;

export default function Teams() {
  const { t } = useTranslation();
  const [teams, setTeams] = useState<Team[]>([]);
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [filterCompetitionId, setFilterCompetitionId] = useState<number | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');

  // Wizard state
  const [showWizardModal, setShowWizardModal] = useState(false);
  const [wizardStep, setWizardStep] = useState<1 | 2>(1);
  const [wizardCompetitionId, setWizardCompetitionId] = useState<number | null>(null);
  const [wizardTeams, setWizardTeams] = useState<WizardTeam[]>([]);
  const [wizardSelected, setWizardSelected] = useState<Set<string>>(new Set());
  const [isLoadingWizard, setIsLoadingWizard] = useState(false);
  const [isImportingWizard, setIsImportingWizard] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<TeamForm>({
    resolver: zodResolver(teamSchema),
  });

  const watchedLogoUrl = watch('logo_url');

  const fetchData = async () => {
    try {
      const [teamsData, compsData] = await Promise.all([
        teamsService.list({ competition_id: filterCompetitionId }),
        competitionsService.list(),
      ]);
      setTeams(teamsData);
      setCompetitions(compsData);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterCompetitionId]);

  const openCreateModal = () => {
    setEditingTeam(null);
    reset({
      competition_id: filterCompetitionId || undefined,
      name: '',
      short_name: '',
      logo_url: '',
    });
    setShowModal(true);
  };

  const openEditModal = (team: Team) => {
    setEditingTeam(team);
    reset({
      competition_id: team.competition_id || undefined,
      name: team.name,
      short_name: team.short_name || '',
      logo_url: team.logo_url || '',
    });
    setShowModal(true);
  };

  const onSubmit = async (data: TeamForm) => {
    try {
      const payload = {
        competition_id: data.competition_id || undefined,
        name: data.name,
        short_name: data.short_name || undefined,
        logo_url: data.logo_url || undefined,
      };

      if (editingTeam) {
        await teamsService.update(editingTeam.id, payload);
        toast.success(t('common.updated'));
      } else {
        await teamsService.create(payload);
        toast.success(t('common.created'));
      }

      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await teamsService.delete(id);
      toast.success(t('common.deleted'));
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleImportFromDazn = () => {
    setShowWizardModal(true);
    setWizardStep(1);
    setWizardCompetitionId(null);
    setWizardTeams([]);
    setWizardSelected(new Set());
  };

  const daznCompetitions = competitions.filter(c => c.dazn_asset_id);

  const handleWizardSelectCompetition = async () => {
    if (!wizardCompetitionId) return;
    setIsLoadingWizard(true);
    try {
      const data = await teamsService.getWizardData(wizardCompetitionId);
      const newTeams = data.filter(t => !t.is_existing);
      setWizardTeams(newTeams);
      setWizardSelected(new Set(newTeams.map(t => t.dazn_asset_id!).filter(Boolean)));
      setWizardStep(2);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('teams.fetchError'));
    } finally {
      setIsLoadingWizard(false);
    }
  };

  const handleWizardImport = async () => {
    if (!wizardCompetitionId || wizardSelected.size === 0) return;
    setIsImportingWizard(true);
    try {
      const toImport = wizardTeams
        .filter(t => t.dazn_asset_id && wizardSelected.has(t.dazn_asset_id))
        .map(t => ({
          dazn_asset_id: t.dazn_asset_id,
          name: t.name,
          logo_url: t.logo_url,
        }));
      const result = await teamsService.importWizard(toImport, wizardCompetitionId);
      toast.success(result.message || t('teams.importedCount', { count: toImport.length }));
      setShowWizardModal(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('teams.importError'));
    } finally {
      setIsImportingWizard(false);
    }
  };

  // Collapsed sections + bulk delete confirm
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState<string | null>(null);

  const handleBulkDeleteCompetition = async (competitionId: number) => {
    try {
      const msg = await teamsService.deleteByCompetition(competitionId);
      toast.success(msg);
      setBulkDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('teams.deleteError'));
    }
  };

  const toggleSection = (key: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const filteredTeams = teams.filter(
    (team) =>
      team.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (team.short_name && team.short_name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Group teams by competition
  const groupedTeams = filteredTeams.reduce<Record<string, { competitionName: string; sportName: string; teams: Team[] }>>((acc, team) => {
    const key = team.competition_id ? String(team.competition_id) : '_none';
    if (!acc[key]) {
      acc[key] = {
        competitionName: team.competition_name || t('teams.noCompetition'),
        sportName: team.sport_name || '',
        teams: [],
      };
    }
    acc[key].teams.push(team);
    return acc;
  }, {});

  // Sort groups: competitions with names first, then "no competition"
  const sortedGroups = Object.entries(groupedTeams).sort(([a], [b]) => {
    if (a === '_none') return 1;
    if (b === '_none') return -1;
    return groupedTeams[a].competitionName.localeCompare(groupedTeams[b].competitionName);
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">{t('teams.title')}</h1>
          <p className="text-dark-400 mt-1">{t('teams.configuredCount', { count: teams.length })}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleImportFromDazn}
            className="btn-secondary"
          >
            <Download size={18} />
            {t('teams.importFromDazn')}
          </button>
          <button onClick={openCreateModal} className="btn-primary">
            <Plus size={18} />
            {t('teams.add')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-3">
          <Filter size={18} className="text-dark-400" />
          <select
            value={filterCompetitionId || ''}
            onChange={(e) => setFilterCompetitionId(e.target.value ? Number(e.target.value) : undefined)}
            className="input w-auto"
          >
            <option value="">{t('teams.allCompetitions')}</option>
            {competitions.map((comp) => (
              <option key={comp.id} value={comp.id}>{comp.name}</option>
            ))}
          </select>
        </div>
        <div className="relative flex-1 max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-400" />
          <input
            type="text"
            placeholder={t('common.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10"
          />
        </div>
      </div>

      {/* Grouped by Competition */}
      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-6 bg-dark-700 rounded w-48 mb-4" />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {[...Array(4)].map((_, j) => (
                  <div key={j} className="h-14 bg-dark-700 rounded" />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : filteredTeams.length === 0 ? (
        <div className="card">
          <p className="text-dark-400">{t('common.noData')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedGroups.map(([groupKey, group]) => {
            const isCollapsed = collapsedSections.has(groupKey);
            return (
              <div key={groupKey} className="card">
                {/* Competition Header */}
                <div className="flex items-center justify-between mb-3">
                  <button
                    onClick={() => toggleSection(groupKey)}
                    className="flex items-center gap-2 flex-1"
                  >
                    <Trophy size={16} className="text-primary-600" />
                    <h3 className="font-medium text-dark-100">{group.competitionName}</h3>
                    {group.sportName && (
                      <span className="text-xs text-dark-500">({group.sportName})</span>
                    )}
                    <span className="badge-primary text-xs">{group.teams.length}</span>
                  </button>
                  <div className="flex items-center gap-2">
                    {groupKey !== '_none' && (
                      bulkDeleteConfirm === groupKey ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleBulkDeleteCompetition(Number(groupKey))}
                            className="p-1.5 rounded-lg bg-error-600 text-white hover:bg-error-500"
                            title={t('common.confirmDelete')}
                          >
                            <Check size={14} />
                          </button>
                          <button
                            onClick={() => setBulkDeleteConfirm(null)}
                            className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400"
                            title={t('common.cancel')}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setBulkDeleteConfirm(groupKey)}
                          className="p-1.5 rounded-lg hover:bg-error-50 text-dark-400 hover:text-error-600"
                          title={t('teams.deleteAllTeams')}
                        >
                          <Trash2 size={14} />
                        </button>
                      )
                    )}
                    <button onClick={() => toggleSection(groupKey)}>
                      {isCollapsed ? (
                        <ChevronRight size={16} className="text-dark-400" />
                      ) : (
                        <ChevronDown size={16} className="text-dark-400" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Team Grid */}
                {!isCollapsed && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {group.teams.map((team) => (
                      <div
                        key={team.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-dark-800 border border-dark-700"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          {team.logo_url ? (
                            <img
                              src={team.logo_url}
                              alt={team.name}
                              className="w-9 h-9 rounded-lg object-contain bg-dark-700 flex-shrink-0"
                            />
                          ) : (
                            <div className="w-9 h-9 rounded-lg bg-dark-700 flex items-center justify-center text-dark-400 flex-shrink-0">
                              {team.short_name?.charAt(0) || team.name.charAt(0)}
                            </div>
                          )}
                          <div className="min-w-0">
                            <h4 className="font-medium text-dark-100 text-sm truncate">{team.name}</h4>
                            {team.short_name && (
                              <p className="text-dark-500 text-xs">{team.short_name}</p>
                            )}
                          </div>
                        </div>
                        {deleteConfirm === team.id ? (
                          <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                            <button
                              onClick={() => handleDelete(team.id)}
                              className="p-1.5 rounded-lg bg-error-600 text-white hover:bg-error-500"
                            >
                              <Check size={14} />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                            <button
                              onClick={() => openEditModal(team)}
                              className="p-1.5 rounded-lg hover:bg-dark-600 text-dark-400 hover:text-dark-100"
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(team.id)}
                              className="p-1.5 rounded-lg hover:bg-error-50 text-dark-400 hover:text-error-600"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowModal(false)}
        >
          <div className="card w-full max-w-xl m-4 animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-dark-100">
                {editingTeam ? editingTeam.name : t('teams.add')}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              {/* Logo preview + Competition */}
              <div className="flex gap-5">
                {/* Logo preview */}
                <div className="flex-shrink-0">
                  {watchedLogoUrl ? (
                    <img
                      src={watchedLogoUrl}
                      alt="Logo"
                      className="w-20 h-20 rounded-lg object-contain bg-dark-700 border border-dark-600"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-lg bg-dark-700 border border-dark-600 flex items-center justify-center text-dark-500 text-xs">
                      {t('teams.noLogo')}
                    </div>
                  )}
                </div>

                {/* Competition + Name side */}
                <div className="flex-1 space-y-3">
                  <div>
                    <label className="label">{t('teams.competition')}</label>
                    <select className="input" {...register('competition_id')}>
                      <option value="">{t('teams.noCompetitionOption')}</option>
                      {competitions.map((comp) => (
                        <option key={comp.id} value={comp.id}>{comp.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Name + Short Name in 2 columns */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('common.name')} *</label>
                  <input
                    type="text"
                    className={errors.name ? 'input-error' : 'input'}
                    placeholder="Juventus"
                    {...register('name')}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-error-500">
                      {t(errors.name.message || '')}
                    </p>
                  )}
                </div>
                <div>
                  <label className="label">{t('teams.shortName')}</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="JUV"
                    {...register('short_name')}
                  />
                </div>
              </div>

              {/* Logo URL */}
              <div>
                <label className="label">{t('teams.logoUrl')}</label>
                <input
                  type="url"
                  className="input"
                  placeholder="https://images.discovery.indazn.com/..."
                  {...register('logo_url')}
                />
                <p className="text-xs text-dark-500 mt-1">
                  {t('teams.logoUrlHelp')}
                </p>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-4 border-t border-dark-700">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary"
                >
                  {isSubmitting ? t('common.loading') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Wizard Modal */}
      {showWizardModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowWizardModal(false)}
        >
          <div className="card w-full max-w-lg m-4 animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-dark-100">
                {t('teams.importTeams')}
              </h2>
              <button
                onClick={() => setShowWizardModal(false)}
                className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400"
              >
                <X size={20} />
              </button>
            </div>

            {/* Step indicators */}
            <div className="flex items-center gap-2 mb-6">
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                wizardStep === 1 ? 'bg-primary-600 text-white' : 'bg-dark-700 text-dark-400'
              }`}>
                1. {t('teams.competitionStep')}
              </span>
              <ChevronRight size={14} className="text-dark-500" />
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                wizardStep === 2 ? 'bg-primary-600 text-white' : 'bg-dark-700 text-dark-400'
              }`}>
                2. {t('teams.selectTeamsStep')}
              </span>
            </div>

            {/* Step 1: Select competition */}
            {wizardStep === 1 && (
              <div className="space-y-4">
                {daznCompetitions.length === 0 ? (
                  <div className="text-center py-8 text-dark-400">
                    <p>{t('teams.noCompetitionsWithDazn')}</p>
                    <p className="text-sm mt-1">{t('teams.importCompetitionsFirst')}</p>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="label">{t('teams.selectCompetition')}</label>
                      <select
                        value={wizardCompetitionId || ''}
                        onChange={(e) => setWizardCompetitionId(e.target.value ? Number(e.target.value) : null)}
                        className="input"
                      >
                        <option value="">{t('common.select')}</option>
                        {daznCompetitions.map((comp) => (
                          <option key={comp.id} value={comp.id}>
                            {comp.name} {comp.sport_name ? `(${comp.sport_name})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => setShowWizardModal(false)}
                        className="btn-secondary"
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        type="button"
                        onClick={handleWizardSelectCompetition}
                        disabled={!wizardCompetitionId || isLoadingWizard}
                        className="btn-primary"
                      >
                        {isLoadingWizard ? (
                          <Loader2 size={18} className="animate-spin" />
                        ) : (
                          <ChevronRight size={18} />
                        )}
                        {t('common.next')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Step 2: Select teams */}
            {wizardStep === 2 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-dark-400">
                    {t('teams.newTeamsFound', { count: wizardTeams.length })}
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setWizardSelected(new Set(wizardTeams.map(t => t.dazn_asset_id!).filter(Boolean)))}
                      className="text-xs text-primary-600 hover:text-primary-700"
                    >
                      {t('common.selectAll')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setWizardSelected(new Set())}
                      className="text-xs text-dark-400 hover:text-dark-300"
                    >
                      {t('common.deselectAll')}
                    </button>
                  </div>
                </div>

                {wizardTeams.length === 0 ? (
                  <div className="text-center py-8 text-dark-400">
                    <p>{t('teams.allTeamsImported')}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                    {wizardTeams.map((wt) => {
                      const key = wt.dazn_asset_id || wt.name;
                      const isChecked = wt.dazn_asset_id ? wizardSelected.has(wt.dazn_asset_id) : false;
                      return (
                        <label
                          key={key}
                          className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-all text-sm ${
                            isChecked
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-dark-700 hover:border-dark-600'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {
                              if (!wt.dazn_asset_id) return;
                              const next = new Set(wizardSelected);
                              if (next.has(wt.dazn_asset_id)) {
                                next.delete(wt.dazn_asset_id);
                              } else {
                                next.add(wt.dazn_asset_id);
                              }
                              setWizardSelected(next);
                            }}
                            className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-600 focus:ring-primary-500"
                          />
                          {wt.logo_url && (
                            <img src={wt.logo_url} alt="" className="w-5 h-5 rounded object-contain" />
                          )}
                          <span className="truncate text-dark-200">{wt.name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}

                <div className="flex justify-between gap-3 pt-4 border-t border-dark-700">
                  <button
                    type="button"
                    onClick={() => setWizardStep(1)}
                    className="btn-secondary"
                  >
                    {t('common.back')}
                  </button>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setShowWizardModal(false)}
                      className="btn-secondary"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={handleWizardImport}
                      disabled={wizardSelected.size === 0 || isImportingWizard}
                      className="btn-primary"
                    >
                      {isImportingWizard ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Download size={18} />
                      )}
                      {t('teams.importCount', { count: wizardSelected.size })}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
